import React, { createContext, useCallback, useContext, useState } from 'react';

import { LayoutArea } from '../types';
import { MINIMUM_PANEL_HEIGHTS, useViewportSize } from './ViewportSizeContext';

export interface LayoutStrategy {
  mode: 'panels' | 'tabs' | 'modal';
  visiblePanels: LayoutArea[];
  tabbedAreas: LayoutArea[];
  modalAreas: LayoutArea[];
  canShowInline: (area: LayoutArea) => boolean;
  shouldUseModal: (area: LayoutArea) => boolean;
  convertToTabs: (areas: LayoutArea[]) => LayoutArea[];
}

interface ModalRequest {
  area: LayoutArea;
  content: React.ReactNode;
  title?: string;
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
}

interface LayoutStrategyContextType {
  strategy: LayoutStrategy;
  showModal: (request: ModalRequest) => void;
  hideModal: () => void;
  activeModal: ModalRequest | null;
  recalculateStrategy: (requestedAreas: LayoutArea[]) => LayoutStrategy;
}

const LayoutStrategyContext = createContext<LayoutStrategyContextType | null>(null);

// Priority order for panel display (higher priority = more likely to stay as panel)
const PANEL_PRIORITY = {
  [LayoutArea.CONTENT]: 100,
  [LayoutArea.HEADER]: 90,
  [LayoutArea.TABS]: 85,
  [LayoutArea.LEFT_SIDEBAR]: 80,
  [LayoutArea.RIGHT_SIDEBAR]: 75,
  [LayoutArea.ACTION_BUTTONS]: 70,
  [LayoutArea.FOOTER]: 65,
  [LayoutArea.SECONDARY_TABS]: 60,
  [LayoutArea.BREADCRUMB]: 55,
  [LayoutArea.SUBHEADER]: 50,
  [LayoutArea.BOTTOM_PANEL]: 45,
  [LayoutArea.TOP_PANEL]: 40,
} as const;

export const LayoutStrategyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { viewport, getMinimumSpaceRequired, canFitInViewport } = useViewportSize();
  const [activeModal, setActiveModal] = useState<ModalRequest | null>(null);

  const calculateLayoutStrategy = useCallback(
    (requestedAreas: LayoutArea[]): LayoutStrategy => {
      if (requestedAreas.length === 0) {
        return {
          mode: 'panels',
          visiblePanels: [],
          tabbedAreas: [],
          modalAreas: [],
          canShowInline: () => false,
          shouldUseModal: () => true,
          convertToTabs: () => [],
        };
      }

      // Sort areas by priority
      const sortedAreas = [...requestedAreas].sort((a, b) => (PANEL_PRIORITY[b] || 0) - (PANEL_PRIORITY[a] || 0));

      const visiblePanels: LayoutArea[] = [];
      const tabbedAreas: LayoutArea[] = [];
      const modalAreas: LayoutArea[] = [];

      // Determine strategy based on viewport
      const { recommendedLayout, availableHeight, isMobile } = viewport;

      if (recommendedLayout === 'modal-only') {
        // Very constrained space - everything goes to modal except content
        modalAreas.push(...sortedAreas.filter((area) => area !== LayoutArea.CONTENT));
        if (sortedAreas.includes(LayoutArea.CONTENT)) {
          visiblePanels.push(LayoutArea.CONTENT);
        }
      } else if (recommendedLayout === 'tabbed' || isMobile) {
        // Limited space - use tabs for secondary content
        let usedHeight = 0;

        for (const area of sortedAreas) {
          const requiredHeight = MINIMUM_PANEL_HEIGHTS[area as unknown as keyof typeof MINIMUM_PANEL_HEIGHTS] || 50;

          if (usedHeight + requiredHeight <= availableHeight * 0.8) {
            // Use 80% of available height
            visiblePanels.push(area);
            usedHeight += requiredHeight;
          } else if (area === LayoutArea.LEFT_SIDEBAR || area === LayoutArea.RIGHT_SIDEBAR) {
            // Convert sidebars to tabs on mobile/limited space
            tabbedAreas.push(area);
          } else {
            modalAreas.push(area);
          }
        }
      } else {
        // Desktop with good space - try to show everything as panels
        let usedHeight = 0;

        for (const area of sortedAreas) {
          const requiredHeight = MINIMUM_PANEL_HEIGHTS[area as unknown as keyof typeof MINIMUM_PANEL_HEIGHTS] || 50;

          if (usedHeight + requiredHeight <= availableHeight * 0.9) {
            // Use 90% of available height
            visiblePanels.push(area);
            usedHeight += requiredHeight;
          } else {
            // If we can't fit as panel, try tabbed approach for similar areas
            if (area === LayoutArea.SECONDARY_TABS || area === LayoutArea.ACTION_BUTTONS) {
              tabbedAreas.push(area);
            } else {
              modalAreas.push(area);
            }
          }
        }
      }

      const mode = modalAreas.length > 0 ? 'modal' : tabbedAreas.length > 0 ? 'tabs' : 'panels';

      return {
        mode,
        visiblePanels,
        tabbedAreas,
        modalAreas,
        canShowInline: (area: LayoutArea) => visiblePanels.includes(area) || tabbedAreas.includes(area),
        shouldUseModal: (area: LayoutArea) => modalAreas.includes(area),
        convertToTabs: (areas: LayoutArea[]) => areas.filter((area) => tabbedAreas.includes(area)),
      };
    },
    [viewport, getMinimumSpaceRequired],
  );

  const [strategy, setStrategy] = useState<LayoutStrategy>(() => calculateLayoutStrategy([]));

  const recalculateStrategy = useCallback(
    (requestedAreas: LayoutArea[]): LayoutStrategy => {
      const newStrategy = calculateLayoutStrategy(requestedAreas);
      setStrategy(newStrategy);
      return newStrategy;
    },
    [calculateLayoutStrategy],
  );

  const showModal = useCallback((request: ModalRequest) => {
    setActiveModal(request);
  }, []);

  const hideModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  return (
    <LayoutStrategyContext.Provider
      value={{
        strategy,
        showModal,
        hideModal,
        activeModal,
        recalculateStrategy,
      }}
    >
      {children}
    </LayoutStrategyContext.Provider>
  );
};

export const useLayoutStrategy = (): LayoutStrategyContextType => {
  const context = useContext(LayoutStrategyContext);
  if (!context) {
    throw new Error('useLayoutStrategy must be used within a LayoutStrategyProvider');
  }
  return context;
};

// Hook for automatically converting panel to modal when space is insufficient
export const useSmartPanelDisplay = (area: LayoutArea, content: React.ReactNode, title?: string) => {
  const { strategy, showModal } = useLayoutStrategy();

  const displayContent = useCallback(() => {
    if (strategy.shouldUseModal(area)) {
      showModal({
        area,
        content,
        title: title || `${area} Content`,
        size: area === LayoutArea.CONTENT ? 'fullscreen' : 'large',
      });
      return null; // Don't render inline
    }
    return content; // Render inline
  }, [area, content, title, strategy, showModal]);

  return {
    shouldRenderInline: strategy.canShowInline(area),
    shouldUseModal: strategy.shouldUseModal(area),
    displayContent,
  };
};
